import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

import Button from '../button';

interface CameraCaptureProps {
  facingMode?: 'user' | 'environment';
  onConfirm: (image: string) => void;
  overlay?: React.ReactNode;
  previewOverlay?: React.ReactNode;
  isSubmitting?: boolean;
  backButton?: React.ReactNode;
  retryButtonText?: string;
  confirmButtonText?: string;
}

const CameraCapture = forwardRef((props: CameraCaptureProps, ref) => {
  const {
    facingMode = 'environment',
    onConfirm,
    overlay,
    isSubmitting,
    previewOverlay,
    backButton,
    retryButtonText,
    confirmButtonText,
  } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);

  const isStartingRef = useRef(false);

  const startWebcam = async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    try {
      stopWebcam();

      const constraints: MediaStreamConstraints = {
        video: {
          width: { ideal: 3000 },
          height: { ideal: 2000 },
          facingMode: { ideal: facingMode },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        if (canvasRef.current) {
          canvasRef.current.width = 0;
          canvasRef.current.height = 0;
        }
      }

      setMediaStream(stream);
    } catch (error) {
      console.error('Error accessing webcam', error);
    } finally {
      isStartingRef.current = false;
    }
  };
  const stopWebcam = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context && video.videoWidth && video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageDataUrl = canvas.toDataURL('image/jpeg');
        setCapturedImage(imageDataUrl);
        stopWebcam();
      }
    }
  };

  useImperativeHandle(ref, () => ({
    startWebcam,
    stopWebcam,
    captureImage,
    reset,
  }));

  const reset = () => {
    stopWebcam();
    setCapturedImage(null);
    startWebcam();
  };

  useEffect(() => {
    startWebcam();
    return () => {
      stopWebcam();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='flex w-full grow flex-col items-center justify-center overflow-hidden'>
      {capturedImage ? (
        <div className='relative size-full'>
          {previewOverlay ? (
            <div className='absolute inset-0 z-10 flex items-center justify-center'>
              {previewOverlay}
            </div>
          ) : null}
          <img src={capturedImage} alt='captured-image' className='size-full object-cover' />
          <div className='absolute bottom-0 z-10 flex w-full items-center justify-center gap-4 bg-white p-4 pb-8'>
            <Button onClick={reset} disabled={isSubmitting} variant='outlined' className='w-full'>
              {retryButtonText ?? 'Retake'}
            </Button>
            <Button
              onClick={() => {
                if (capturedImage) {
                  onConfirm(capturedImage);
                }
              }}
              disabled={isSubmitting}
              className='w-full'
            >
              {confirmButtonText ?? 'Confirm'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className='relative size-full'>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              controls={false}
              className='size-full object-cover'
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
            />
            {overlay ? (
              <div className='absolute inset-0 z-10 flex size-full items-center justify-center'>
                {overlay}
              </div>
            ) : null}
            <div className='absolute bottom-4 z-10 flex w-full flex-row items-center justify-between'>
              {!mediaStream ? (
                <>
                  <div className='flex w-24' />
                  <Button onClick={startWebcam}>Start Webcam</Button>
                  <div className='flex w-24' />
                </>
              ) : (
                <>
                  <div className='flex w-24'>{backButton}</div>
                  <button
                    onClick={captureImage}
                    className='flex size-20 items-center justify-center rounded-full bg-gray-100'
                    type='button'
                  >
                    <div className='size-[68px] rounded-full bg-white' />
                  </button>
                  <div className='flex w-24' />
                </>
              )}
            </div>
          </div>
          {mediaStream ? <canvas ref={canvasRef} className='hidden' /> : null}
        </>
      )}
    </div>
  );
});

CameraCapture.displayName = 'CameraCapture';

export default CameraCapture;
